import type { JSX } from 'react';
import { GLYPHS } from './icons.generated';

/**
 * Иконка из библиотеки проекта.
 *
 * Сами глифы лежат в `assets/icons/*.svg` и собираются в `icons.generated.tsx`
 * скриптом `scripts/build-icons.mjs` — один источник на всё приложение,
 * добавить иконку значит положить файл и пересобрать.
 *
 * Цвета у иконки своего нет: в файлах она чёрная, сборка меняет чёрный
 * на `currentColor`, и глиф красится тем же цветом, что и текст рядом.
 * §12 оставляет цвет за статусом — синяя иконка на серой кнопке спорила бы
 * с красным «не работает».
 *
 * Своя система координат у каждого глифа: набор собран из сеток 16, 24
 * и 512, и приводить их к одной значило бы перерисовывать чужую графику.
 * Толщину штриха сборка уже привела к общей.
 *
 * Размер один на всё приложение — 16 px, и переопределять его в местах вызова
 * не нужно: разнобой в размерах читается как небрежность быстрее, чем любой
 * отдельно взятый размер кажется удачным. Исключений два, оба названы
 * в `assets/icons.md`: полоса свёрнутого меню, где иконка осталась от пункта
 * одна, и знак продукта — см. `BRAND_ICON_SIZE`.
 */
export type IconName = keyof typeof GLYPHS;

/**
 * Размер знака продукта — 24 px.
 *
 * Знак не иконка действия: он не подписан и не стоит в ряду себе подобных,
 * по которому глаз выравнивает размер. Он отвечает на вопрос «что это
 * за платформа», задают его один раз при входе, и в 16 px три грани куба
 * сливаются в пятно. К тому же знак занимает не всю свою сетку — 19,5
 * из 24 по ширине, — и на общих 16 px рисуется меньше любой соседней иконки.
 *
 * Константой, а не числом по месту: знак стоит в шапке и на экране входа,
 * и разъезжаться этим двум местам нельзя.
 */
export const BRAND_ICON_SIZE = 24;

export function Icon({ name, size = 16 }: { name: IconName; size?: number }): JSX.Element {
  const glyph = GLYPHS[name];

  return (
    <svg
      width={size}
      height={size}
      viewBox={glyph.viewBox}
      // Заливка снята на корне: контурные глифы рисуются штрихом, а сплошные
      // включают заливку сами, обёрткой вокруг своих фигур.
      fill="none"
      // Смысл несёт подпись кнопки: иконка здесь — опора для глаза,
      // а не источник информации, и скринридеру её читать незачем.
      aria-hidden="true"
      focusable="false"
      style={{ flex: 'none' }}
    >
      {glyph.body}
    </svg>
  );
}
